/**
 * INHYRDA — gästerna och vad de rapporterat. Prototypens data-screen="inhyrda".
 *
 * BESLUT 17: gästen är anställd i ETT ANNAT FÖRETAG, ofta en konkurrent. Han är
 * gratis hos Klartid (PAID_ROLES saknar 'guest'), han har ett eget org.nr, och
 * han ser bara de arbetsordrar han tilldelats. Skärmens hela nytta är den chefen
 * beskriver själv: han ser timmarna INNAN inhyrningsfirmans faktura kommer.
 *
 * DÄRFÖR GRUPPERAS DET PÅ ORG.NR OCH INTE PÅ PERSON. Två man från samma
 * plattsättningsfirma kommer på EN faktura, så det är org.nr som är
 * motparten. guest_org_number står på member-raden; MOTPARTENS NAMN finns inte
 * i kontraktet — prototypen skriver "Plattsättning AB", men det fältet finns
 * ingenstans att hämta. Se luckorna i överlämningen; tills det finns står
 * numret, och det är i alla fall sant.
 *
 * TIMMAR, INTE PENGAR. Laget är enda stället i appen där priser finns, så det
 * VORE tillåtet att visa belopp här. Det görs ändå inte: det frysta timpriset på
 * en gästs tidrad är vad DU tar av DIN kund för hans timme (beslut 15), inte vad
 * inhyrningsfirman tar av dig. Ett sådant tal bredvid rubriken "jämför med deras
 * faktura" hade varit fel siffra på rätt plats — den värsta sorten. Beloppen
 * finns i Att fakturera, där de betyder just det de ser ut att betyda.
 */

import { useMemo, useState } from 'react';
import type { MemberRow, TimeEntryRow, WorkOrderRow } from '@delat/types';
import { useInloggad } from '../app/session';
import { visaMinuter } from '../app/datum';
import {
  Felruta,
  Knapp,
  Laddar,
  Listrad,
  Segment,
  Sifferblock,
  Tomtext,
  Yta,
} from '../ui';
import {
  PERIODVALEN,
  periodFor,
  useFirmadata,
  visaOrgnummer,
  visaPersoner,
} from './LagetData';
import type { Periodval } from './LagetData';

interface Orderrad {
  readonly id: string;
  readonly titel: string;
  readonly minuter: number;
  readonly personer: readonly string[];
}

interface Grupp {
  readonly nyckel: string;
  readonly rubrik: string;
  readonly minuter: number;
  readonly ordrar: readonly Orderrad[];
  readonly personer: readonly string[];
}

const UTAN_ORGNUMMER = 'utan-orgnr';

const ordertitel = (order: WorkOrderRow | undefined): string => {
  if (order === undefined) return 'Borttagen arbetsorder';
  // Adressen först: det är så en hantverkare känner igen ett jobb (Orderkort).
  return order.address ?? order.description;
};

const byggGrupper = (
  gaster: readonly MemberRow[],
  tidrader: readonly TimeEntryRow[],
  ordrar: ReadonlyMap<string, WorkOrderRow>,
): readonly Grupp[] => {
  const grupper = new Map<string, { rubrik: string; gaster: MemberRow[] }>();
  for (const gast of gaster) {
    const nyckel = gast.guest_org_number ?? UTAN_ORGNUMMER;
    const rubrik =
      gast.guest_org_number === null
        ? 'Utan organisationsnummer'
        : visaOrgnummer(gast.guest_org_number);
    const fanns = grupper.get(nyckel);
    if (fanns === undefined) grupper.set(nyckel, { rubrik, gaster: [gast] });
    else fanns.gaster.push(gast);
  }

  const ut: Grupp[] = [];
  for (const [nyckel, grupp] of grupper) {
    const konton = new Set(grupp.gaster.map((g) => g.account_id as string));
    const mina = tidrader.filter((t) => konton.has(t.account_id));

    const perOrder = new Map<string, { minuter: number; konton: Set<string> }>();
    for (const rad of mina) {
      const post = perOrder.get(rad.work_order_id) ?? { minuter: 0, konton: new Set<string>() };
      post.minuter += Number(rad.minutes);
      post.konton.add(rad.account_id);
      perOrder.set(rad.work_order_id, post);
    }

    const namnFor = (id: string): string =>
      grupp.gaster.find((g) => g.account_id === id)?.name ?? 'Okänd';

    const orderrader: Orderrad[] = [...perOrder.entries()]
      .map(([id, post]) => ({
        id,
        titel: ordertitel(ordrar.get(id)),
        minuter: post.minuter,
        personer: [...post.konton].map(namnFor).sort((a, b) => a.localeCompare(b, 'sv')),
      }))
      .sort((a, b) => b.minuter - a.minuter || a.titel.localeCompare(b.titel, 'sv'));

    ut.push({
      nyckel,
      rubrik: grupp.rubrik,
      minuter: orderrader.reduce((s, o) => s + o.minuter, 0),
      ordrar: orderrader,
      personer: grupp.gaster.map((g) => g.name).sort((a, b) => a.localeCompare(b, 'sv')),
    });
  }

  return ut.sort((a, b) => b.minuter - a.minuter || a.rubrik.localeCompare(b.rubrik, 'sv'));
};

export const Inhyrda = () => {
  const { firmaId } = useInloggad();
  const [period, sattPeriod] = useState<Periodval>('12v');
  const [omgang, sattOmgang] = useState(0);
  const lage = useFirmadata(firmaId, omgang);

  const intervall = useMemo(() => periodFor(period), [period]);

  const grupper = useMemo<readonly Grupp[]>(() => {
    if (lage.status !== 'klar') return [];
    const d = lage.data;
    const gaster = d.medlemmar.filter((m) => m.role === 'guest');
    const iPerioden = d.tidrader.filter(
      (t) => t.work_date >= intervall.from_date && t.work_date <= intervall.to_date,
    );
    const ordrar = new Map(d.arbetsordrar.map((o) => [o.id as string, o]));
    return byggGrupper(gaster, iPerioden, ordrar);
  }, [lage, intervall]);

  const antalGaster =
    lage.status === 'klar'
      ? lage.data.medlemmar.filter((m) => m.role === 'guest').length
      : 0;
  const tilldelade =
    lage.status === 'klar' ? lage.data.gasttilldelningar.length : 0;
  const totalt = grupper.reduce((s, g) => s + g.minuter, 0);
  const medTimmar = grupper.filter((g) => g.minuter > 0);
  const utanTimmar = grupper.filter((g) => g.minuter === 0);

  return (
    <>
      <Segment
        val={PERIODVALEN}
        valt={period}
        onValj={sattPeriod}
        etikett="Period"
      />

      {lage.status === 'laddar' && <Laddar rader={4} />}

      {lage.status === 'fel' && (
        <Felruta
          fel={lage.fel}
          atgard={
            <Knapp variant="piller" onClick={() => sattOmgang((n) => n + 1)}>
              Försök igen
            </Knapp>
          }
        />
      )}

      {lage.status === 'klar' && (
        <>
          <Yta>
            <Sifferblock
              varde={visaMinuter(totalt)}
              under={`Rapporterat av inhyrda · senaste ${intervall.text}`}
            />
          </Yta>

          {antalGaster === 0 && (
            <Tomtext>
              Ingen inhyrd är upplagd. En inhyrd läggs in med sitt EGET företags
              organisationsnummer, rapporterar gratis och ser bara de arbetsordrar du
              tilldelat honom.
            </Tomtext>
          )}

          {medTimmar.map((grupp) => (
            <Yta
              key={grupp.nyckel}
              ikon="inhyrda"
              rubrik={grupp.rubrik}
              rakning={visaMinuter(grupp.minuter)}
            >
              {grupp.ordrar.map((order) => (
                <Listrad
                  key={order.id}
                  titel={order.titel}
                  bi={visaPersoner(order.personer.length)}
                  under={order.personer.join(' · ')}
                  varde={visaMinuter(order.minuter)}
                />
              ))}
            </Yta>
          ))}

          {utanTimmar.length > 0 && (
            <Yta rubrik="Inga timmar i perioden" rakning={`${utanTimmar.length} st`} tyst>
              {utanTimmar.map((grupp) => (
                <Listrad
                  key={grupp.nyckel}
                  titel={grupp.rubrik}
                  under={grupp.personer.join(' · ')}
                  varde={visaMinuter(0)}
                />
              ))}
            </Yta>
          )}

          {antalGaster > 0 && (
            <Tomtext>
              {`${visaPersoner(antalGaster)} upplagda som inhyrda, ${tilldelade} tilldelningar till arbetsordrar. `}
              Inhyrda rapporterar gratis och ser bara sina egna jobb. Timmarna här är
              deras egna rapporter — jämför dem med fakturan innan du betalar den.
            </Tomtext>
          )}
        </>
      )}
    </>
  );
};
